import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/pro-regular-svg-icons/faDownload';
import { Button } from 'Button';
import { Text } from 'Text';
import { Avatar } from 'Avatar';
import { formattedBrandName } from 'AppBranding';
import { getBranding, getProgramTitle } from 'Cohorts';
import { useRequiredCurrentUser } from 'FrontRoyalAngular';
import { getProgramConfigValue, type WelcomePackageConfiguration, type WelcomePackageStudent } from 'Program';
import { getCohort } from 'Users';
import profilePhotoDefault from 'vectors/profile_photo_default_slate_grey.svg';
import { useLaunchProgramGuide } from '../useLaunchProgramGuide';

const PREFIX = 'settings.paid_program_welcome_package';

export function PaidProgramWelcomePackage(): ReactElement | null {
    const { t } = useTranslation('back_royal');
    const currentUser = useRequiredCurrentUser();
    const launchProgramGuide = useLaunchProgramGuide();
    const cohort = getCohort(currentUser);

    if (!cohort) return null;

    const config = getProgramConfigValue<WelcomePackageConfiguration | null>(
        cohort.programType,
        'welcomePackageConfiguration',
    );
    if (!config) return null;

    const brandNameShort = formattedBrandName('short', getBranding(cohort));

    // EMBA14 registered before we had a list of classmates to show.
    const headlineKey = cohort.name === 'EMBA14' ? 'headline_few_of_your_registered_classmates' : config.headlineKey;

    const standaloneShortProgramTitle = getProgramConfigValue<string>(
        cohort.programType,
        'standaloneShortProgramTitle',
    );

    return (
        <div className="mt-10">
            <section>
                {headlineKey ? (
                    <Text as="h3" variant="h2" className="mb-6 text-center">
                        {t(`${PREFIX}.${headlineKey}`, {
                            programTitle: getProgramTitle(cohort) ?? '',
                            program: standaloneShortProgramTitle ?? '',
                        })}
                    </Text>
                ) : null}

                {/*
                    Two off-scale values from the design: a 60px avatar becomes h-16, and #757068 for
                    the education line becomes slate-grey.
                */}
                <ul className="grid list-none grid-cols-1 gap-6 p-0 sm:grid-cols-2 sm:gap-x-8">
                    {config.students.map(student => (
                        <StudentProfile key={student.name} student={student} />
                    ))}
                </ul>
            </section>

            {cohort.programGuideUrl ? (
                <section className="mt-10">
                    <Text as="h3" variant="h2" className="mb-6 text-center">
                        {t(`${PREFIX}.headline_meet_additional_students`)}
                    </Text>

                    <div className="text-center">
                        <Button
                            type="button"
                            color="primary-gradient"
                            height={18}
                            icon={
                                config.badgeSrc ? (
                                    <img src={config.badgeSrc} alt="" aria-hidden="true" className="h-12" />
                                ) : (
                                    <FontAwesomeIcon icon={faDownload} />
                                )
                            }
                            onPress={launchProgramGuide}
                        >
                            {t(`${PREFIX}.download_program_guide`)}
                        </Button>
                    </div>
                </section>
            ) : null}

            {config.videoSrc ? (
                <section className="mt-10">
                    <hr className="mb-10 border-slate-grey-200" />
                    <Text as="h3" variant="h2" className="mb-6 text-center">
                        {t(`${PREFIX}.headline_video`, { brandName: brandNameShort })}
                    </Text>
                    <div className="relative aspect-video overflow-hidden">
                        <iframe
                            src={config.videoSrc}
                            title={t(`${PREFIX}.headline_video`, { brandName: brandNameShort })}
                            allowFullScreen
                            className="absolute inset-0 h-full w-full border-0"
                        />
                    </div>
                </section>
            ) : null}
        </div>
    );
}

function StudentProfile({ student }: { student: WelcomePackageStudent }): ReactElement {
    return (
        <li className="flex gap-3 text-start sm:gap-5 sm:last:odd:col-span-2 sm:last:odd:justify-self-center">
            <Avatar
                src={student.avatarUrl}
                name={student.name}
                showFallback
                fallback={<img src={profilePhotoDefault} alt="" />}
                className="h-16 w-16"
            />

            <div className="min-w-0 flex-1">
                <Text as="div" className="text-black/80">
                    {student.name}
                </Text>

                <Text as="div" weight="semibold" className="text-xs text-black">
                    {student.jobTitle} @ {student.company}
                </Text>

                <Text as="div" className="text-xs">
                    {student.degree}, {student.university}
                </Text>

                <Text as="div" className="text-xs text-black/80">
                    {student.locationString}
                </Text>
            </div>
        </li>
    );
}
